
import React, { useState } from 'react';
import { apiFetch } from '../utils/api';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Alert,
  Box,
} from '@mui/material';
import { Download, ContentCopy } from '@mui/icons-material';
import jsPDF from 'jspdf';
import { Link } from 'react-router-dom';

const Search = () => {
  const [imei, setImei] = useState('');
  const [gadget, setGadget] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTime, setSearchTime] = useState('');
  const [limitedAccess, setLimitedAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearch = async () => {
    if (!imei) return alert('Please enter an IMEI or Serial Number.');

    setIsSearching(true);
    setGadget(null);
    setErrorMessage('');
    setLimitedAccess(false);

    try {
      // 🔍 Search for gadget details
      const response = await apiFetch(`/api/gadgets/${imei}`);

      if (response.success) {
        setGadget(response.data);
        setSearchTime(new Date().toLocaleString());
        setLimitedAccess(response.limitedAccess || false);
      } else {
        setErrorMessage(response.message || 'No results found.');
      }

      // 📝 Log search query
      await apiFetch('/api/search', 'POST', { searchTerm: imei });

    } catch (err) {
      console.error('Search error:', err);
      setErrorMessage(err.message || 'An error occurred during the search.');
    } finally {
      setIsSearching(false);
    }
  };

  const downloadPDF = () => {
    const pdf = new jsPDF();
    pdf.setFontSize(12);
    pdf.text('Gadget Details', 10, 10);
    pdf.text(`Device Type: ${gadget.deviceType}`, 10, 20);
    pdf.text(`Brand: ${gadget.brand}`, 10, 30);
    pdf.text(`Model: ${gadget.model}`, 10, 40);
    pdf.text(`Color: ${gadget.color}`, 10, 50);
    pdf.text(`Serial: ${gadget.serialNumber || 'N/A'}`, 10, 60);

    if (!limitedAccess) {
      const owner = gadget.ownerDetails || {};
      pdf.text(`Owner: ${owner.name || 'N/A'}`, 10, 70);
      pdf.text(`Email: ${owner.email || 'N/A'}`, 10, 80);
      pdf.text(`Phone: ${owner.telephone || 'N/A'}`, 10, 90);
      pdf.text(`Address: ${owner.address || 'N/A'}`, 10, 100);
    } else {
      pdf.text(`Note: Subscribe to unlock full gadget status.`, 10, 70);
    }

    pdf.text(`Status: ${gadget.status || 'N/A'}`, 10, 110);
    pdf.text(`Provided by: ProTechTion.co.zw`, 10, 120);
    pdf.text(`Search Time: ${searchTime}`, 10, 130);
    pdf.save('gadget-details.pdf');
  };

  const copyToClipboard = () => {
    const owner = gadget.ownerDetails || {};
    const content = `
Gadget Details:
Device Type: ${gadget.deviceType}
Brand: ${gadget.brand}
Model: ${gadget.model}
Color: ${gadget.color}
Serial Number: ${gadget.serialNumber}
${
      limitedAccess
        ? 'Note: Subscribe to unlock full status and owner info.'
        : `Status: ${gadget.status || 'N/A'}\nOwner: ${owner.name || 'N/A'}\nEmail: ${owner.email || 'N/A'}\nPhone: ${owner.telephone || 'N/A'}\nAddress: ${owner.address || 'N/A'}`
    }
Search Time: ${searchTime}
Provided by: ProTechTion.co.zw
`;
    navigator.clipboard.writeText(content)
      .then(() => alert('Results copied to clipboard!'))
      .catch(() => alert('Failed to copy.'));
  };

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        Gadget Search
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <TextField
          label="IMEI or Serial Number"
          variant="outlined"
          fullWidth
          value={imei}
          onChange={(e) => setImei(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch} disabled={isSearching}>
          {isSearching ? <CircularProgress size={24} /> : 'Search'}
        </Button>
      </Box>

      {errorMessage && <Alert severity="error" sx={{ mb: 3 }}>{errorMessage}</Alert>}

      {gadget && (
        <Paper elevation={4} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom color="green">
            Gadget Found ✅
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><strong>Device Type:</strong> {gadget.deviceType}</Grid>
            <Grid item xs={12} sm={6}><strong>Brand:</strong> {gadget.brand}</Grid>
            <Grid item xs={12} sm={6}><strong>Model:</strong> {gadget.model}</Grid>
            <Grid item xs={12} sm={6}><strong>Color:</strong> {gadget.color}</Grid>
            <Grid item xs={12} sm={6}><strong>Serial Number:</strong> {gadget.serialNumber}</Grid>
            <Grid item xs={12} sm={6}><strong>Status:</strong> {gadget.status || 'N/A'}</Grid>

            {!limitedAccess && (
              <>
                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
                <Grid item xs={12}><Typography variant="subtitle1"><strong>Owner Details</strong></Typography></Grid>
                <Grid item xs={12} sm={6}><strong>Name:</strong> {gadget.ownerDetails?.name || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><strong>Email:</strong> {gadget.ownerDetails?.email || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><strong>Phone:</strong> {gadget.ownerDetails?.telephone || 'N/A'}</Grid>
                <Grid item xs={12} sm={6}><strong>Address:</strong> {gadget.ownerDetails?.address || 'N/A'}</Grid>
              </>
            )}

            {limitedAccess && (
              <Grid item xs={12}>
                <Alert severity="info">
                  You are viewing limited information. <Link to="/subscribe">Subscribe</Link> for full access.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} mt={2}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Download />}
                sx={{ mr: 2 }}
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<ContentCopy />}
                onClick={copyToClipboard}
              >
                Copy Details
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box mt={5}>
        <Button component={Link} to="/" variant="outlined" color="secondary">
          ← Back to Homepage
        </Button>
      </Box>
    </Container>
  );
};

export default Search;

